import Text from 'UIKit/Text'
import Flex from 'UIKit/Box/Flex'
import { MenuItem as MenuItemConfig } from 'config/types'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import { FlexProps } from 'styled-system'
import { NextLinkFromReactRouter } from 'UIKit/NextLink'
import { getIcon, Icons } from 'svgs'
import { useMemo, useState } from 'react'
import Box from 'UIKit/Box/Box'
import Image from 'UIKit/Image'
import { useAuth } from 'state/auth/hooks'

export const StyledContainer = styled(Flex)<{ $Active?: boolean; $Small?: boolean }>`
  padding: 0px 12px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radii.tiny};
  align-items: center;
  justify-content: ${({ $Small }) => ($Small ? 'center' : 'space-between')};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  ${({ theme: { mediaQueries } }) => mediaQueries.sm} {
    max-width: 212px;
  }

  &:hover {
    ${({ $Small, theme }) =>
      !$Small
        ? css`
            background: ${theme.colors.gradient.hover};
          `
        : css`
            background: ${theme.colors.backgroundAlt5};
          `};
  }

  svg:not(.stroke) {
    fill: ${({ $Active, theme }) => ($Active ? theme.colors.text : theme.colors.textSubtle)};
  }

  ${Text} {
    color: ${({ $Active, theme }) => ($Active ? theme.colors.text : theme.colors.textSubtle)};
  }

  ${({ $Active, $Small, theme }) =>
    $Active &&
    (!$Small
      ? css`
          background: ${theme.colors.gradient.hover};
        `
      : css`
          background: ${theme.colors.backgroundAlt5};
        `)};
`

const StyledExpansitionIconContainer = styled.div<{ $Open: boolean }>`
  transition: ${({ theme }) => theme.transitions.medium};
  transform: ${({ $Open }) => (!$Open ? 'rotate(180deg)' : '')};
`

const StyledExpansitionContent = styled(Box)`
  transition: ease-out ${({ theme }) => theme.transitions.medium};
  overflow: hidden;
`

const MenuItem: React.FC<
  { id?: string; item: MenuItemConfig; activeRoute?: string; small?: boolean; attributes?: any } & FlexProps
> = ({ id, item, activeRoute, small, attributes = {} }) => {
  const { t } = useTranslation()
  const { hasSession } = useAuth()

  const Icon = getIcon(item.icon)
  const icon = Icon ? <Icon /> : <Image width={24} height={24} src={item.icon} alt={item.content} />

  if (item.ExpandComponent && !small) {
    return <MenuExpansionItem item={item} active={activeRoute === item.route} />
  }

  const subItems = useMemo(() => {
    return item.subMenu ? item.subMenu.filter((subItem) => !(!hasSession && subItem.onlySignedUser)) : []
  }, [hasSession])

  return (
    !(!hasSession && item.onlySignedUser) && (
      <Box
        background={
          subItems.length > 0
            ? 'linear-gradient(90deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.00) 100%)'
            : ''
        }
        borderRadius="4px"
        maxWidth={['100%', '', 212]}
      >
        <StyledContainer
          mb="2px"
          as={item.external ? 'a' : NextLinkFromReactRouter}
          to={item.route}
          href={item.route}
          target={item.external ? '_blank' : ''}
          $Active={activeRoute === item.route}
          $Small={small}
          id={id}
          data-tooltip-id={small ? 'common-tip-right' : ''}
          data-tooltip-content={t(item.content)}
          height={item.subMenu ? '41px !important' : '48px !important'}
          {...(attributes || {})}
        >
          <Flex alignItems="center">
            {icon}
            {!small && (
              <>
                <Text fontWeight="600" fontSize="14px" ml="8px" letterSpacing={-0.28} lineHeight="normal">
                  {t(item.content)}
                </Text>
                {item.isNew && (
                  <Flex ml="12px" alignItems="center">
                    <Icons.NewIcon />
                  </Flex>
                )}
              </>
            )}
          </Flex>
        </StyledContainer>

        {!small &&
          subItems.map((subMenu) => (
            <StyledContainer
              mb="2px"
              key={subMenu.route}
              as={subMenu.external ? 'a' : NextLinkFromReactRouter}
              to={subMenu.route}
              href={subMenu.route}
              target={subMenu.external ? '_blank' : ''}
              $Active={activeRoute === subMenu.route}
              $Small={false}
              id={id}
              height="41px !important"
              {...(subMenu?.attributes || {})}
            >
              <Flex ml="24px" alignItems="center">
                <Text fontWeight="600" fontSize="14px" ml="8px" lineHeight="normal" letterSpacing={-0.28}>
                  {t(subMenu.content)}
                </Text>
                {subMenu.isNew && (
                  <Flex ml="12px" alignItems="center">
                    <Icons.NewIcon />
                  </Flex>
                )}
              </Flex>
            </StyledContainer>
          ))}
      </Box>
    )
  )
}

export const MenuExpansionItem: React.FC<{ item: MenuItemConfig; active?: boolean } & FlexProps> = ({
  item,
  active,
}) => {
  const Icon = getIcon(item.icon)
  const icon = Icon ? <Icon /> : <Image width={24} height={24} src={item.icon} alt={item.content} />
  const { t } = useTranslation()

  const [isExpanded, setIsExpanded] = useState(true)

  const toggleContent = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <Box>
      <StyledContainer mb="2px" $Active={active} onClick={toggleContent}>
        <Flex alignItems="center">
          {icon}
          <Text fontWeight="600" fontSize="14px" ml="8px">
            {t(item.content)}
          </Text>
        </Flex>
        <StyledExpansitionIconContainer $Open={isExpanded}>
          <Icons.ExpandIcon />
        </StyledExpansitionIconContainer>
      </StyledContainer>

      <StyledExpansitionContent maxHeight={!isExpanded ? '0px' : '256px'} mb={isExpanded ? '4px' : '0px'}>
        <item.ExpandComponent />
      </StyledExpansitionContent>
    </Box>
  )
}

export default MenuItem
